import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["aria-current", "aria-describedby", "aria-label", "aria-labelledby", "children", "className", "disabled", "download", "endIcon", "href", "hrefLang", "id", "layoutId", "onClick", "onPress", "onPressStart", "onPressEnd", "ping", "referrerPolicy", "rel", "render", "size", "startIcon", "style", "target", "weight", "xstyle"],
  _excluded2 = ["ref"];
import { c as _c } from "react-compiler-runtime";
import { forwardRef } from 'react';
import { useRender } from '@base-ui/react/use-render';
import * as stylex from '@stylexjs/stylex';
import { assertElementRender } from '../../utils/assertElementRender';
import { useLayoutConstraints } from '../layout/LayoutConstraints';
import { focusRingOutset } from '../../styles/focus-ring';
import { linkTextDecoration } from '../../styles/link-text-decoration';
import { warnMutuallyExclusive } from '../../utils/warnMutuallyExclusive';
import { DATA_COMPONENT_ATTRIBUTE } from '../../constants/dataAttributes';
import { useLink } from '../../hooks/useLink/useLink';
import { SlotWrapper } from '../shared/SlotWrapper';
import { LinkExternalIcon } from 'trellis-assets/icons/components/LinkExternalIcon';
import { theme } from 'trellis-theming/modern-theme.stylex';
import { theme as experimentalTheme } from 'trellis-theming/experimental-theme.stylex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const COMPONENT_NAME = 'Link';

/**
 * Canonical font-weight variants for Link.
 *
 * - `default` — the standard link weight (500).
 * - `subtle` — a de-emphasized, lighter link weight (400).
 *
 * Note: the underlying tokens are still named `regular` (400) and `strong` (500).
 * The prop values describe *semantic intent* rather than the raw token tier, so
 * `default` consumes the `strong` token and `subtle` consumes the `regular` token.
 * See `typographyStyles` below for the mapping. TODO(token-typography): rename the tokens
 * to align with these values once UX signs off.
 */

const linkStyles = {
  base: {
    borderRadius: "x1nq0khy",
    color: "x5ekyyb xxyvr2x x12ghts7 xcg86uu",
    cursor: "x1ypdohk",
    outline: "x1a2a7pz",
    transitionDuration: "xx6bhzk x12w9bfk",
    transitionProperty: "xz1df8t",
    $$css: true
  },
  disabled: {
    color: "xw3glo5",
    cursor: "x1h6gzvc",
    $$css: true
  },
  icon: {
    alignItems: "x6s0dn4",
    color: "x1heor9g",
    display: "x3nfvp2",
    flexShrink: "x2lah0s",
    justifyContent: "xl56j7k",
    verticalAlign: "x1x7wgjd",
    $$css: true
  },
  iconDisabled: {
    color: "xw3glo5",
    $$css: true
  }
};

// NOTE: The style keys use the new semantic names (`default` / `subtle`), but the
// tokens they consume are still named `regular` (400) and `strong` (500). Until the
// tokens are renamed, the mapping is inverted from what you'd expect:
//   - `*-default` reads the `*-strong` token (500 = the new default link weight)
//   - `*-subtle`  reads the `*-regular` token (400 = the de-emphasized weight)
// TODO(token-typography): swap these token references once the token names are updated.
const typographyStyles = {
  "lg-default": {
    fontFamily: "xmd9qwa",
    fontSize: "xc9lazt",
    fontWeight: "xfbzod6",
    letterSpacing: "xnjrnmo",
    lineHeight: "xbcj7cu",
    $$css: true
  },
  "lg-subtle": {
    fontFamily: "xfxe8sg",
    fontSize: "x1a7hurv",
    fontWeight: "xfgrtbu",
    letterSpacing: "x1zvb9h",
    lineHeight: "x1urda5o",
    $$css: true
  },
  "md-default": {
    fontFamily: "xq33457",
    fontSize: "x115jnxo",
    fontWeight: "x9x4hfr",
    letterSpacing: "x1q73f03",
    lineHeight: "xg0k5v1",
    $$css: true
  },
  "md-subtle": {
    fontFamily: "x6hfyku",
    fontSize: "xe639uq",
    fontWeight: "x1h7urat",
    letterSpacing: "x1ibk7ho",
    lineHeight: "x1x6p1lu",
    $$css: true
  },
  "sm-default": {
    fontFamily: "x19fxltq",
    fontSize: "x1ci4r37",
    fontWeight: "xwnwj42",
    letterSpacing: "xkpnzk4",
    lineHeight: "x12arpqd",
    $$css: true
  },
  "sm-subtle": {
    fontFamily: "xg149o6",
    fontSize: "xhdlvez",
    fontWeight: "x1my9jzc",
    letterSpacing: "x1a3cg6y",
    lineHeight: "xgyutik",
    $$css: true
  }
};
const iconSizeStyles = {
  lg: {
    height: "x1bxwfjw",
    width: "x1ehcoyr",
    $$css: true
  },
  md: {
    height: "x3nka1z",
    width: "xuie3is",
    $$css: true
  },
  sm: {
    height: "x1k7vh3a",
    width: "x1hqqe46",
    $$css: true
  }
};
const endIconGapStyles = {
  lg: {
    marginInlineStart: "xlv0wks",
    marginLeft: null,
    marginRight: null,
    $$css: true
  },
  md: {
    marginInlineStart: "x1fchzdb",
    marginLeft: null,
    marginRight: null,
    $$css: true
  },
  sm: {
    marginInlineStart: "x1fchzdb",
    marginLeft: null,
    marginRight: null,
    $$css: true
  }
};
const startIconGapStyles = {
  lg: {
    marginInlineEnd: "x731szm",
    marginLeft: null,
    marginRight: null,
    $$css: true
  },
  md: {
    marginInlineEnd: "x10djnuo",
    marginLeft: null,
    marginRight: null,
    $$css: true
  },
  sm: {
    marginInlineEnd: "x10djnuo",
    marginLeft: null,
    marginRight: null,
    $$css: true
  }
};

/**
 * Used to navigate to another page, a section within the page, or an external resource. For non-navigational actions, use Button instead.
 *
 * This component is part of Trellis's compositive API layer. Prefer the config
 * API, which covers the majority of use cases. Only reach for this component
 * when the config API can't express your pattern, and share your use case in
 * #trellis-support — it directly informs how we evolve the config layer.
 *
 * @see {@link __LinkConfig | Link}
 */
export const Link = /*#__PURE__*/forwardRef((t0, forwardedRef) => {
  const $ = _c(80);
  let ariaCurrent;
  let ariaDescribedBy;
  let ariaLabel;
  let ariaLabelledBy;
  let children;
  let download;
  let endIcon;
  let href;
  let hrefLang;
  let id;
  let layoutId;
  let onClick;
  let onPress;
  let onPressEnd;
  let onPressStart;
  let ping;
  let referrerPolicy;
  let rel;
  let render;
  let restProps;
  let startIcon;
  let t1;
  let t2;
  let t3;
  let target;
  let xstyle;
  if ($[0] !== t0) {
    const {
        "aria-current": t4,
        "aria-describedby": t5,
        "aria-label": t6,
        "aria-labelledby": t7,
        children: t8,
        disabled: t9,
        download: t10,
        endIcon: t11,
        href: t12,
        hrefLang: t13,
        id: t14,
        layoutId: t15,
        onClick: t16,
        onPress: t17,
        onPressStart: t18,
        onPressEnd: t19,
        ping: t20,
        referrerPolicy: t21,
        rel: t22,
        render: t23,
        size: t24,
        startIcon: t25,
        target: t26,
        weight: t27,
        xstyle: t28
      } = t0,
      t29 = _objectWithoutPropertiesLoose(t0, _excluded);
    ariaCurrent = t4;
    ariaDescribedBy = t5;
    ariaLabel = t6;
    ariaLabelledBy = t7;
    children = t8;
    t1 = t9;
    download = t10;
    endIcon = t11;
    href = t12;
    hrefLang = t13;
    id = t14;
    layoutId = t15;
    onClick = t16;
    onPress = t17;
    onPressStart = t18;
    onPressEnd = t19;
    ping = t20;
    referrerPolicy = t21;
    rel = t22;
    render = t23;
    t2 = t24;
    startIcon = t25;
    target = t26;
    t3 = t27;
    xstyle = t28;
    restProps = t29;
    $[0] = t0;
    $[1] = ariaCurrent;
    $[2] = ariaDescribedBy;
    $[3] = ariaLabel;
    $[4] = ariaLabelledBy;
    $[5] = children;
    $[6] = download;
    $[7] = endIcon;
    $[8] = href;
    $[9] = hrefLang;
    $[10] = id;
    $[11] = layoutId;
    $[12] = onClick;
    $[13] = onPress;
    $[14] = onPressEnd;
    $[15] = onPressStart;
    $[16] = ping;
    $[17] = referrerPolicy;
    $[18] = rel;
    $[19] = render;
    $[20] = restProps;
    $[21] = startIcon;
    $[22] = t1;
    $[23] = t2;
    $[24] = t3;
    $[25] = target;
    $[26] = xstyle;
  } else {
    ariaCurrent = $[1];
    ariaDescribedBy = $[2];
    ariaLabel = $[3];
    ariaLabelledBy = $[4];
    children = $[5];
    download = $[6];
    endIcon = $[7];
    href = $[8];
    hrefLang = $[9];
    id = $[10];
    layoutId = $[11];
    onClick = $[12];
    onPress = $[13];
    onPressEnd = $[14];
    onPressStart = $[15];
    ping = $[16];
    referrerPolicy = $[17];
    rel = $[18];
    render = $[19];
    restProps = $[20];
    startIcon = $[21];
    t1 = $[22];
    t2 = $[23];
    t3 = $[24];
    target = $[25];
    xstyle = $[26];
  }
  const disabled = t1 === undefined ? false : t1;
  const size = t2 === undefined ? "md" : t2;
  const weight = t3 === undefined ? "default" : t3;
  assertElementRender("Link", render);
  warnMutuallyExclusive("Link", render, xstyle);
  let t4;
  if ($[27] !== layoutId) {
    t4 = {
      layoutId
    };
    $[27] = layoutId;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  const {
    layoutConstraintStyles
  } = useLayoutConstraints(t4);
  let t5;
  if ($[29] !== ariaCurrent || $[30] !== ariaDescribedBy || $[31] !== ariaLabel || $[32] !== ariaLabelledBy || $[33] !== download || $[34] !== hrefLang || $[35] !== id || $[36] !== onClick || $[37] !== ping || $[38] !== referrerPolicy || $[39] !== rel || $[40] !== restProps || $[41] !== target) {
    t5 = Object.assign({
      target,
      rel,
      download,
      onClick,
      id,
      ping,
      hrefLang,
      referrerPolicy,
      "aria-current": ariaCurrent,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      "aria-describedby": ariaDescribedBy
    }, restProps);
    $[29] = ariaCurrent;
    $[30] = ariaDescribedBy;
    $[31] = ariaLabel;
    $[32] = ariaLabelledBy;
    $[33] = download;
    $[34] = hrefLang;
    $[35] = id;
    $[36] = onClick;
    $[37] = ping;
    $[38] = referrerPolicy;
    $[39] = rel;
    $[40] = restProps;
    $[41] = target;
    $[42] = t5;
  } else {
    t5 = $[42];
  }
  let t6;
  if ($[43] !== disabled || $[44] !== href || $[45] !== onPress || $[46] !== onPressEnd || $[47] !== onPressStart || $[48] !== t5) {
    t6 = {
      href,
      isDisabled: disabled,
      onPress,
      onPressStart,
      onPressEnd,
      props: t5
    };
    $[43] = disabled;
    $[44] = href;
    $[45] = onPress;
    $[46] = onPressEnd;
    $[47] = onPressStart;
    $[48] = t5;
    $[49] = t6;
  } else {
    t6 = $[49];
  }
  const {
    getLinkProps
  } = useLink(t6);
  let t7;
  if ($[50] !== getLinkProps) {
    t7 = getLinkProps();
    $[50] = getLinkProps;
    $[51] = t7;
  } else {
    t7 = $[51];
  }
  let linkProps;
  if ($[52] !== t7) {
    const t8 = _objectWithoutPropertiesLoose(t7, _excluded2);
    linkProps = t8;
    $[52] = t7;
    $[53] = linkProps;
  } else {
    linkProps = $[53];
  }
  const resolvedStartIcon = startIcon;
  let t8;
  if ($[54] !== endIcon || $[55] !== target) {
    var _endIcon;
    t8 = (_endIcon = endIcon) !== null && _endIcon !== void 0 ? _endIcon : target === "_blank" ? /*#__PURE__*/_jsx(LinkExternalIcon, {}) : undefined;
    $[54] = endIcon;
    $[55] = target;
    $[56] = t8;
  } else {
    t8 = $[56];
  }
  const resolvedEndIcon = t8;
  let t9;
  if ($[57] !== children || $[58] !== disabled || $[59] !== resolvedEndIcon || $[60] !== resolvedStartIcon || $[61] !== size) {
    t9 = resolvedStartIcon != null || resolvedEndIcon != null ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [resolvedStartIcon != null && /*#__PURE__*/_jsxs("span", Object.assign({}, {
        className: "xuxw1ft"
      }, {
        children: [/*#__PURE__*/_jsx(SlotWrapper, {
          as: "span",
          xstyle: [linkStyles.icon, disabled && linkStyles.iconDisabled, iconSizeStyles[size], startIconGapStyles[size]],
          children: resolvedStartIcon
        }), "\u2060"]
      })), children, resolvedEndIcon != null && /*#__PURE__*/_jsxs("span", Object.assign({}, {
        className: "xuxw1ft"
      }, {
        children: ["\u2060", /*#__PURE__*/_jsx(SlotWrapper, {
          as: "span",
          xstyle: [linkStyles.icon, disabled && linkStyles.iconDisabled, iconSizeStyles[size], endIconGapStyles[size]],
          children: resolvedEndIcon
        })]
      }))]
    }) : children;
    $[57] = children;
    $[58] = disabled;
    $[59] = resolvedEndIcon;
    $[60] = resolvedStartIcon;
    $[61] = size;
    $[62] = t9;
  } else {
    t9 = $[62];
  }
  const composedChildren = t9;
  const t10 = render;
  let t11;
  if ($[63] !== forwardedRef) {
    t11 = [forwardedRef];
    $[63] = forwardedRef;
    $[64] = t11;
  } else {
    t11 = $[64];
  }
  let t12;
  if ($[65] !== disabled || $[66] !== layoutConstraintStyles || $[67] !== render || $[68] !== size || $[69] !== weight || $[70] !== xstyle) {
    t12 = stylex.props(render == null && linkStyles.base, render == null && linkTextDecoration.default, render == null && typographyStyles[`${size}-${weight}`], render == null && disabled && linkStyles.disabled, render == null && disabled && linkTextDecoration.disabled, render == null && focusRingOutset.default, render == null && layoutConstraintStyles, render == null ? xstyle : undefined);
    $[65] = disabled;
    $[66] = layoutConstraintStyles;
    $[67] = render;
    $[68] = size;
    $[69] = weight;
    $[70] = xstyle;
    $[71] = t12;
  } else {
    t12 = $[71];
  }
  let t13;
  if ($[72] !== composedChildren || $[73] !== linkProps || $[74] !== t12) {
    t13 = Object.assign({
      children: composedChildren
    }, linkProps, t12, {
      [DATA_COMPONENT_ATTRIBUTE]: COMPONENT_NAME
    });
    $[72] = composedChildren;
    $[73] = linkProps;
    $[74] = t12;
    $[75] = t13;
  } else {
    t13 = $[75];
  }
  const t14 = t13;
  let t15;
  if ($[76] !== t10 || $[77] !== t11 || $[78] !== t14) {
    t15 = {
      defaultTagName: "a",
      render: t10,
      ref: t11,
      props: t14
    };
    $[76] = t10;
    $[77] = t11;
    $[78] = t14;
    $[79] = t15;
  } else {
    t15 = $[79];
  }
  return useRender(t15);
});
Link.displayName = COMPONENT_NAME;